import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import BaseDialog from '../BaseDialog';
import ResultReviewTimer from '../ResultReviewTimer';
import useApiControl from '../../../common/useApiControl';

const parseChoices = (answers) => {
  if (!answers) {
    return [];
  }

  try {
    return JSON.parse(answers);
  } catch (error) {
    console.error('Error parsing answers JSON', error);
    return [];
  }
};

const ConfirmRoundResultDialog = ({ isVisible, onClose, round, refreshKey, onEdit, onConfirmed, dialogMode, containerRef }) => {
  const [review, setReview] = useState(null);
  const [message, setMessage] = useState('');
  const [nowMs, setNowMs] = useState(Date.now());
  const choices = useMemo(() => parseChoices(round?.answers), [round?.answers]);
  const resultKey = review?.result !== null && review?.result !== undefined
    ? String(review.result)
    : round?.result !== null && round?.result !== undefined
      ? String(round.result)
      : '';
  const resultLabel = resultKey !== '' ? (review?.answers ?? choices)[Number(resultKey)] : '';
  const reviewUntil = review?.result_review_until ?? round?.result_review_until;
  const activeDisputes = Array.isArray(review?.active_disputes) ? review.active_disputes : [];
  const reviewStatus = review?.review_status ?? round?.review_status;
  const canConfirmByTime = reviewUntil
    ? new Date(reviewUntil).getTime() <= nowMs
    : false;
  const isHeld = reviewStatus === 'held';
  const disabled = resultKey === '' || !canConfirmByTime || isHeld;
  const isReviewAccepting = Boolean(reviewUntil) && !canConfirmByTime;
  const guidanceText = isHeld
    ? '再確認依頼が基準に達したため、このままでは確定できません。\n結果を訂正するか、このラウンドを不成立にしてください。'
    : isReviewAccepting
      ? 'メンバーから再確認の依頼が届く場合があります。\n受付時間が終わるまでお待ちください。'
      : activeDisputes.length > 0
        ? '再確認依頼があります。\n内容を確認し、問題がなければこの結果で確定してください。'
        : canConfirmByTime
          ? '再確認の受付は終了しました。\n問題がなければ、この結果で確定してください。'
          : '再確認の受付状況を確認しています。';

  const params = useMemo(() => ({
    eid: round?.event_id
  }), [round?.event_id]);

  const reviewParams = useMemo(() => ({
    event_id: round?.event_id,
    number: round?.number
  }), [round?.event_id, round?.number]);

  const { executeRequest } = useApiControl({
    action: '/confirm_round_result',
    params,
    method: 'POST',
  });

  const { executeRequest: fetchReview } = useApiControl({
    action: '/get_round_result_review',
    params: reviewParams,
    method: 'POST',
  });

  useEffect(() => {
    if (!isVisible || !round?.event_id || !round?.number) {
      return;
    }

    setNowMs(Date.now());
    setMessage('');
    fetchReview().then((data) => {
      setReview(data);
    }).catch((error) => {
      setMessage(error?.response?.data?.message || '確認状況の取得に失敗しました。');
    });
  }, [isVisible, round?.event_id, round?.number, refreshKey]);

  useEffect(() => {
    if (!isVisible || !reviewUntil || canConfirmByTime) {
      return undefined;
    }

    const reviewEndMs = new Date(reviewUntil).getTime();
    const delay = Math.max(reviewEndMs - Date.now(), 0);
    const timeoutId = setTimeout(() => {
      setNowMs(Date.now());
    }, delay + 50);

    return () => clearTimeout(timeoutId);
  }, [isVisible, reviewUntil, canConfirmByTime]);

  const handleConfirm = () => {
    executeRequest().then(() => {
      onClose();
      onConfirmed();
    }).catch((error) => {
      setMessage(error?.response?.data?.message || '正式確定に失敗しました。');
      fetchReview().then((data) => setReview(data)).catch(() => {});
    });
  };

  const handleEdit = () => {
    onClose();
    onEdit();
  };

  return (
    <BaseDialog isVisible={isVisible} onClose={onClose} dialogMode={dialogMode} containerRef={containerRef}>
      <Box sx={{ width: 360, maxWidth: '100%', p: 1 }}>
        <Typography variant="h6" sx={{ fontSize: 17, mb: 0.75 }}>
          選択結果の確認
        </Typography>
        {message && <Alert severity="warning" sx={{ mb: 1.5 }}>{message}</Alert>}
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            確定する結果
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>
            {resultLabel || '未選択'}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 14, color: isHeld ? 'error.main' : 'text.secondary', mb: 1, whiteSpace: 'pre-line' }}>
          {guidanceText}
        </Typography>
        <Box sx={{ mb: 1.5 }}>
          {!isHeld && (
            <ResultReviewTimer
              reviewUntil={reviewUntil}
              totalSeconds={review?.result_review_seconds}
            />
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: reviewUntil ? 1 : 0 }}>
            再確認依頼：{activeDisputes.length}件
          </Typography>
          {activeDisputes.length > 0 && (
            <Box sx={{ mt: 0.75 }}>
              {activeDisputes.map((dispute) => (
                <Typography key={`${dispute.user_id}-${dispute.created_at}`} variant="body2" color="text.secondary">
                  {dispute.name}: {(review?.answers ?? choices)[Number(dispute.claimed_result)] ?? '不明'}
                </Typography>
              ))}
            </Box>
          )}
          {!isHeld && !canConfirmByTime && reviewUntil && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              受付時間が終わると確定できます。
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap', mt: 1 }}>
          {isHeld && (
            <Button variant="outlined" onClick={handleEdit}>
              訂正・不成立へ
            </Button>
          )}
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirm}
            disabled={disabled}
          >
            この結果で確定
          </Button>
        </Box>
      </Box>
    </BaseDialog>
  );
};

export default ConfirmRoundResultDialog;
